import { ProposalFund, Scheme } from "creso-core/types";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { useFetchFundByIsin } from "creso-core/hooks";
import { envLogger } from "@/utils/feature-flags";
import { Activity, useCallback, useMemo, useState } from "react";
import { formatAsPercentage } from "creso-core/utils";
import { calculateWeightedAverages } from "@planning/config";
import { BasketPicker } from "@transactions/components/import-collection";
import { useCollectionGroups } from "@transactions/hooks/use-collection-groups";
import { ShoppingBasketIcon } from "lucide-react";
import { Badge, Button, cn } from "web-design-system";
import {
  ProposalFormSchema,
  useProposalBuilderContext,
} from "../../providers/proposal-provider";
import { AddNewSchemeButton, EMPTY_FUND_SCHEME_ID, FundRow } from "./fund-row";

type FundType = "LUMPSUM" | "SIP";

const FUND_TYPE_CONFIG = {
  LUMPSUM: {
    schemeType: "all" as const,
    amountField: "lumpsum_amount" as const,
    emptyRow: { sip_amount: "0" as const, lumpsum_amount: "" as const },
    newFundAmounts: () => ({
      sip_amount: "0" as const,
      lumpsum_amount: "0",
    }),
  },
  SIP: {
    schemeType: "sip" as const,
    amountField: "sip_amount" as const,
    emptyRow: { sip_amount: "" as const, lumpsum_amount: "0" as const },
    newFundAmounts: () => ({
      sip_amount: "" as string,
      lumpsum_amount: "" as string,
    }),
  },
} satisfies Record<FundType, unknown>;

interface FundSelectionProps {
  type: FundType;
}

export function FundSelection({ type }: FundSelectionProps) {
  const config = FUND_TYPE_CONFIG[type];

  type ProposalFundRow = z.infer<
    typeof ProposalFormSchema
  >["proposal_funds"][number];

  const { collectionId } = useProposalBuilderContext();

  const { control, getValues, setValue } =
    useFormContext<z.infer<typeof ProposalFormSchema>>();

  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: "proposal_funds",
  });

  const watchedFunds = useWatch({
    name: "proposal_funds",
    control,
  });

  const [editingBasketId, setEditingBasketId] = useState<number | null>(
    collectionId ?? null
  );

  const {
    collectionGroups,
    otherRows: otherFunds,
    customCollectionKeys,
    handleRemoveCollection,
    handleRemoveScheme,
    handleAmountChange: handleCollectionAmountChange,
    recordOriginalAmounts,
    clearCustomKey,
  } = useCollectionGroups<ProposalFundRow & { id: string }>({
    rows: fields,
    remove,
    getCollection: (r) => r.collection ?? null,
    getSchemeKey: (r) => r.fund_scheme,
    getAmount: (r) => (type === "SIP" ? r.sip_amount : r.lumpsum_amount) ?? "0",
    filter: (r) => r.type === type,
  });

  const hasValidFundsRows = useMemo(
    () => fields.some((f) => f.type === type),
    [fields, type]
  );

  const weightedReturns = useMemo(() => {
    const filtered = (watchedFunds ?? []).filter(
      (f) =>
        f.type === type && f.fund_detail && Number(f[config.amountField]) > 0
    );

    const totalAmount = filtered.reduce(
      (sum, f) => sum + Number(f[config.amountField]),
      0
    );

    if (totalAmount === 0 || filtered.length === 0) return null;

    return calculateWeightedAverages(filtered, type);
  }, [watchedFunds, type, config.amountField]);

  const fetchFundByIsin = useFetchFundByIsin();

  const appendEmptyRow = () => {
    const emptyRow = {
      fund_scheme: EMPTY_FUND_SCHEME_ID,
      amc_code: "",
      fund_scheme_name: "",
      source: "NEW",
      source_folio: null,
      type,
      ...config.emptyRow,
      remarks: "",
      order: fields.length,
    } as const;

    if (!hasValidFundsRows) {
      append(emptyRow);
    }
    append(emptyRow);
  };

  const handleBasketImport = useCallback(
    async (
      results: {
        scheme: Scheme<"all"> | Scheme<"sip">;
        amount: string;
        collection: { name: string; id: number; type: "basket" | "proposal" };
      }[]
    ) => {
      if (results.length === 0) return;

      const importedBasketId = results[0].collection.id;
      const isReplacing =
        editingBasketId != null && editingBasketId === importedBasketId;

      const currentFunds = getValues("proposal_funds");

      const baseFunds = isReplacing
        ? currentFunds.filter(
            (f) => !(f.type === type && f.collection?.id === importedBasketId)
          )
        : currentFunds;

      const startIndex = baseFunds.length;

      const fundsWithIsin = results
        .filter((result) => {
          if (isReplacing) return true;
          const fundSchemeId = result.scheme.fund_scheme_id ?? result.scheme.id;
          return !baseFunds.some(
            (f) =>
              f.type === type &&
              f.fund_scheme === fundSchemeId &&
              f.fund_scheme !== EMPTY_FUND_SCHEME_ID
          );
        })
        .map((result, i) => {
          const fundSchemeId = result.scheme.fund_scheme_id ?? result.scheme.id;
          const roundedAmount = Math.round(Number(result.amount)).toString();
          return {
            fund: {
              fund_scheme: fundSchemeId,
              amc_code: result.scheme.amc_morningstar_code || "",
              fund_scheme_name: result.scheme.scheme_name,
              source: "NEW" as const,
              source_folio: null,
              type,
              sip_amount: type === "SIP" ? roundedAmount : "0",
              lumpsum_amount: type === "LUMPSUM" ? roundedAmount : "0",
              remarks: "",
              order: startIndex + i,
              collection: {
                name: result.collection.name,
                id: result.collection.id,
                type: result.collection.type,
              },
            },
            isin: result.scheme.scheme_isin,
          };
        });

      if (fundsWithIsin.length === 0) {
        setEditingBasketId(null);
        return;
      }

      const fundDetails = await Promise.allSettled(
        fundsWithIsin.map(({ isin }) => fetchFundByIsin(isin))
      );

      const newRows = fundsWithIsin.map(({ fund }, i) => {
        const result = fundDetails[i];
        if (result.status === "rejected") {
          envLogger.warn("Failed to fetch fund details for basket import");
          return fund;
        }
        const detail = result.value;
        const remarks =
          detail.ranking?.pros
            ?.slice(0, 2)
            .map(
              (p: { text: string }, j: number) =>
                `${j === 0 ? "i" : "ii"}) ${p.text}`
            )
            .join(", ") || "";
        return { ...fund, remarks, fund_detail: detail };
      });

      if (isReplacing) {
        const merged = [...baseFunds, ...newRows].map((f, i) => ({
          ...f,
          order: i,
        }));
        replace(merged);
      } else {
        append(newRows);
      }

      clearCustomKey(`collection_${importedBasketId}`);
      recordOriginalAmounts(
        importedBasketId,
        newRows.map((r) => ({
          key: r.fund_scheme,
          amount: type === "SIP" ? r.sip_amount : r.lumpsum_amount,
        }))
      );

      setEditingBasketId(null);
    },
    [
      type,
      getValues,
      append,
      replace,
      fetchFundByIsin,
      editingBasketId,
      clearCustomKey,
      recordOriginalAmounts,
    ]
  );

  const handleSchemeSelect = async (
    index: number,
    scheme: Scheme<"all"> | Scheme<"sip"> | null
  ) => {
    const current = getValues(`proposal_funds.${index}`);

    if (!scheme) {
      update(index, {
        ...current,
        fund_scheme: EMPTY_FUND_SCHEME_ID,
        amc_code: "",
        fund_scheme_name: "",
        fund_detail: undefined,
        remarks: "",
        collection: undefined,
      });
      return;
    }

    const fundSchemeId = scheme.fund_scheme_id ?? scheme.id;

    const isDuplicate = fields.some(
      (f, i) =>
        i !== index &&
        f.type === type &&
        f.fund_scheme === fundSchemeId &&
        f.fund_scheme !== EMPTY_FUND_SCHEME_ID
    );

    if (isDuplicate) return;

    const newFund: z.infer<
      typeof ProposalFormSchema
    >["proposal_funds"][number] = {
      ...current,
      fund_scheme: fundSchemeId,
      amc_code: scheme.amc_morningstar_code || "",
      fund_scheme_name: scheme.scheme_name,
      source: "NEW",
      source_folio: null,
      type,
      ...config.newFundAmounts(),
      remarks: "",
      order: index,
      collection: undefined,
    };

    update(index, { ...newFund, fund_detail: undefined });

    try {
      const result = await fetchFundByIsin(scheme.scheme_isin);
      const remarks =
        result.ranking?.pros
          ?.slice(0, 2)
          .map((p, i) => `${i === 0 ? "i" : "ii"}) ${p.text}`)
          .join(", ") || "";
      setValue(`proposal_funds.${index}.remarks`, remarks, {
        shouldValidate: false,
        shouldTouch: false,
      });
      setValue(`proposal_funds.${index}.fund_detail`, result, {
        shouldValidate: false,
        shouldTouch: false,
      });
    } catch {
      envLogger.warn("Failed to fetch fund by isin", {
        isin: scheme.scheme_isin,
      });
    }
  };

  const handleRemoveFund = (index: number, collectionKey?: string) => {
    if (collectionKey) {
      handleRemoveScheme(index, collectionKey);
    } else {
      remove(index);
    }
  };

  const handleAmountChange = (
    index: number,
    amount: string,
    collectionKey?: string
  ) => {
    setValue(`proposal_funds.${index}.${config.amountField}`, amount);
    if (collectionKey) {
      handleCollectionAmountChange(collectionKey, amount, index);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="relative flex-1">
        <div className="flex flex-col gap-4">
          {!collectionGroups.length || editingBasketId != null ? (
            <BasketPicker
              schemeType={config.schemeType}
              basketId={editingBasketId}
              onCancel={() => setEditingBasketId(null)}
              onImport={handleBasketImport}
              defaultAmount={
                type === "LUMPSUM"
                  ? getValues("total_lumpsum_amount")
                  : getValues("total_sip_amount")
              }
            />
          ) : null}
          {collectionGroups
            .filter(({ collection }) => collection.id !== editingBasketId)
            .map(({ key, schemes, collection }) => (
              <div
                key={key}
                className={cn(
                  "flex w-full flex-col gap-4 rounded-lg border border-border bg-linear-to-r from-tag-yellow to-background px-4 py-3"
                )}
              >
                <div className="flex items-center gap-2">
                  <ShoppingBasketIcon className="size-5 text-tag-yellow-foreground" />
                  <span className="text-sm font-semibold">
                    {collection.name}
                  </span>
                  {customCollectionKeys.has(key) && (
                    <Badge
                      variant="custom"
                      className="bg-tag-slate text-tag-slate-foreground"
                    >
                      Custom
                    </Badge>
                  )}
                  <div className="ml-auto flex items-center gap-6">
                    {!customCollectionKeys.has(key) &&
                      collection.id != null && (
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0 text-xs font-medium hover:underline"
                          onClick={() => setEditingBasketId(collection.id!)}
                        >
                          Edit Total Amount
                        </Button>
                      )}
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs font-medium text-destructive hover:underline"
                      onClick={() => handleRemoveCollection(key, schemes)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  {schemes.map((row) => {
                    return (
                      <FundRow
                        key={fields[row.idx]?.id ?? row.idx}
                        schemeType={config.schemeType}
                        fund={row as unknown as ProposalFund}
                        lockScheme
                        onRemove={() => handleRemoveFund(row.idx, key)}
                        onAmountChange={(amount) =>
                          handleAmountChange(row.idx, amount, key)
                        }
                        onSchemeChange={(scheme) =>
                          handleSchemeSelect(row.idx, scheme)
                        }
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          <div
            className={cn(
              "flex flex-col gap-4",
              collectionGroups.length > 0 && "px-4"
            )}
          >
            {!hasValidFundsRows && (
              <FundRow
                fund={{} as ProposalFund}
                schemeType={config.schemeType}
                onRemove={() => handleRemoveFund(fields.length)}
                onAmountChange={(amount) =>
                  handleAmountChange(fields.length, amount)
                }
                onSchemeChange={(scheme) =>
                  handleSchemeSelect(fields.length, scheme)
                }
              />
            )}
            {otherFunds.map((row) => {
              return (
                <FundRow
                  key={fields[row.idx]?.id ?? row.idx}
                  schemeType={config.schemeType}
                  fund={row as unknown as ProposalFund}
                  onRemove={() => handleRemoveFund(row.idx)}
                  onAmountChange={(amount) =>
                    handleAmountChange(row.idx, amount)
                  }
                  onSchemeChange={(scheme) =>
                    handleSchemeSelect(row.idx, scheme)
                  }
                />
              );
            })}
            <AddNewSchemeButton
              onClick={appendEmptyRow}
              className={cn(collectionGroups.length > 0 && "-mt-1")}
            />
          </div>
        </div>
      </div>
      {weightedReturns && (
        <div className="flex shrink-0 items-center justify-between gap-2 rounded-md border border-border/50 bg-muted/50 px-4 py-3">
          <div
            className={cn(
              "flex items-center gap-2",
              getValues("projected_value")
                ? "flex-col"
                : "flex-row justify-between w-full"
            )}
          >
            <span className="text-xs text-muted-foreground">
              Average historical returns
            </span>
            <div className="flex items-center gap-4">
              {weightedReturns.map(({ label, value }) => (
                <div key={label} className="flex items-baseline-last gap-1.5">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <span className="text-sm font-medium">
                    {value !== null ? formatAsPercentage(value) : "N/A"}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Activity mode={getValues("projected_value") ? "visible" : "hidden"}>
            <span>vs</span>
            <div className="flex flex-col items-center justify-end gap-2">
              <span className="text-xs text-muted-foreground">
                Expected Returns
              </span>
              <div className="flex items-baseline-last gap-1.5">
                <span className="text-sm font-medium">
                  {formatAsPercentage(getValues("expected_return"))}
                </span>
              </div>
            </div>
          </Activity>
        </div>
      )}
    </div>
  );
}
